import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from '../../../core/base/base.service';
import { ConfigModel } from '../../../shared/models/config.model';
import { ConfigKey, ConfigTypeGroup } from '../../../shared/enum/config-key.enum';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConfigService extends BaseService {
  constructor(http: HttpClient) {
    super(http, 'config.label', '/config', ConfigModel);
  }

  // Functions
  getByGroup<T>(group: ConfigTypeGroup): Observable<T> {
    return this.http.get<T>(`${this.uri}/${group}`, { headers: this.defaultHeaders }).pipe(
      map((response: any) => {
        const data =
          response.data.length > 0 ? response.data.map((row: any) => new this.Model(row)) : [];
        return { ...response, data };
      })
    );
  }

  getByStockThreshold(): Observable<{
    lowStockThreshold: number;
    lowSampleStockThreshold: number;
  }> {
    return this.http
      .get<{
        lowStockThreshold: number;
        lowSampleStockThreshold: number;
      }>(`${this.uri}/stock-threshold`, { headers: this.defaultHeaders })
      .pipe(
        map((response: any) => {
          return {
            lowStockThreshold: response?.data?.lowStockThreshold ?? 10,
            lowSampleStockThreshold: response?.data?.lowSampleStockThreshold ?? 5,
          };
        })
      );
  }

  getByShippingRates(): Observable<{
    national: number;
    international: number;
  }> {
    return this.http
      .get<{
        national: number;
        international: number;
      }>(`${this.uri}/shipping`, { headers: this.defaultHeaders })
      .pipe(
        map((response: any) => {
          return {
            national: response?.data?.national ?? 0,
            international: response?.data?.international ?? 0,
          };
        })
      );
  }

  /**
   * The default infrastructure markup (%) a new fabric costing starts from, read off the
   * pricing group — there is no single-key endpoint for it the way there is for the VAT rate.
   *
   * An unset config answers `null`, not `0`: a real 0% is a priced decision, and writing one
   * into a blank costing would quietly claim it had been made.
   */
  getFabricOtherCosts(): Observable<number | null> {
    return this.http
      .get<{ infraMarkup: number }>(`${this.uri}/infrastructure-markup`, {
        headers: this.defaultHeaders,
      })
      .pipe(map((response: any) => response?.data ?? 0));
  }

  getVatRate(): Observable<number> {
    return this.http
      .get<{ vatRate: number }>(`${this.uri}/vat-rate`, { headers: this.defaultHeaders })
      .pipe(map((response: any) => response?.data ?? 0));
  }

  getDtxMarkup(): Observable<number> {
    return this.http
      .get<{ dtx: number }>(`${this.uri}/dtx`, { headers: this.defaultHeaders })
      .pipe(map((response: any) => response?.data ?? 0));
  }

  updateByGroup<T>(
    group: ConfigTypeGroup,
    body: { configs: { key: string; value: any }[] },
    options = {}
  ): Observable<T> {
    return this.http.put<T>(`${this.uri}/${group}`, body, {
      headers: this.defaultHeaders,
      ...options,
    });
  }
}
